import { useState, useEffect, useCallback } from "react";
import { History, RotateCcw, X, Clock, ChevronRight, Loader2, GitBranch } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "a l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

export function VersionHistoryPanel({ diagramId, open, onClose, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [diffVersion, setDiffVersion] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const loadVersions = useCallback(async () => {
    if (!diagramId || !open) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/diagrams/${diagramId}/versions`, { withCredentials: true });
      setVersions(data);
    } catch {
      // silent
    }
    setLoading(false);
  }, [diagramId, open]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const loadDiff = async (idx) => {
    if (diffVersion === idx) { setDiffVersion(null); setDiffData(null); return; }
    setDiffVersion(idx);
    setDiffLoading(true);
    try {
      const { data } = await axios.get(`${API}/diagrams/${diagramId}/versions/${idx}`, { withCredentials: true });
      setDiffData(data);
    } catch {
      setDiffData(null);
    }
    setDiffLoading(false);
  };

  const restore = async (idx) => {
    setRestoring(idx);
    try {
      await axios.post(`${API}/diagrams/${diagramId}/versions/restore`, { version_idx: idx }, { withCredentials: true });
      toast.success("Version restauree ! Rechargement...");
      onRestore?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setRestoring(null);
  };

  // Compute diff between current (idx=0) and selected version
  const computeDiff = (versionData) => {
    if (!versionData || !versions[0]) return null;
    const currentNodes = new Set((versions[0]?.node_count || 0) > 0 ? [] : []);
    const vNodes = versionData.nodes || [];
    const vNodeIds = new Set(vNodes.map(n => n.id));
    return { nodeCount: vNodes.length, edgeCount: (versionData.edges || []).length };
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="version-history-panel">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white border-l border-gray-200 shadow-2xl h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <History size={16} className="text-indigo-600" />
            <h3 className="font-semibold text-sm text-gray-900">Historique des versions</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400">
            <X size={16} />
          </button>
        </div>

        {/* Version list */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-indigo-500" />
            </div>
          )}

          {!loading && versions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <GitBranch size={32} className="text-gray-200 mb-3" />
              <p className="text-sm font-semibold text-gray-500">Aucune version</p>
              <p className="text-xs text-gray-400 mt-1">Les versions sont créées automatiquement a chaque sauvegarde.</p>
            </div>
          )}

          {!loading && versions.length > 0 && (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-7 top-0 bottom-0 w-px bg-gray-200" />

              {versions.map((v, idx) => {
                const isExpanded = diffVersion === idx;
                const isCurrent = idx === 0;
                return (
                  <div key={v.created_at + idx} className="relative" data-testid={`version-${idx}`}>
                    {/* Timeline dot */}
                    <div className={`absolute left-5 top-5 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10 ${
                      isCurrent ? "bg-indigo-600 border-indigo-600" : "bg-white border-gray-300"
                    }`}>
                      {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>

                    <div className="ml-14 mr-4 py-3 border-b border-gray-50">
                      <button
                        onClick={() => loadDiff(idx)}
                        className="w-full text-left group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">
                              {isCurrent && <span className="text-[8px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-bold">ACTUEL</span>}
                              {v.user_name || "Utilisateur"}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                <Clock size={9} /> {timeAgo(v.created_at)}
                              </span>
                              <span className="text-[9px] text-gray-300 bg-gray-100 px-1.5 py-0.5 rounded-full">
                                {v.node_count} nœuds &middot; {v.edge_count} liens
                              </span>
                            </div>
                          </div>
                          <ChevronRight size={12} className={`text-gray-300 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                        </div>
                      </button>

                      {/* Expanded diff view */}
                      {isExpanded && (
                        <div className="mt-3 bg-gray-50 rounded-xl p-3 border border-gray-200 animate-in fade-in">
                          {diffLoading ? (
                            <div className="flex items-center gap-2 py-2">
                              <Loader2 size={12} className="animate-spin text-indigo-500" />
                              <span className="text-xs text-gray-400">Chargement...</span>
                            </div>
                          ) : diffData ? (
                            <>
                              <div className="grid grid-cols-2 gap-2 mb-3">
                                <div className="bg-white rounded-lg p-2.5 border border-gray-200">
                                  <p className="text-[9px] text-gray-400 font-semibold uppercase">Noeuds</p>
                                  <p className="text-lg font-bold font-mono text-gray-900">{(diffData.nodes || []).length}</p>
                                </div>
                                <div className="bg-white rounded-lg p-2.5 border border-gray-200">
                                  <p className="text-[9px] text-gray-400 font-semibold uppercase">Connexions</p>
                                  <p className="text-lg font-bold font-mono text-gray-900">{(diffData.edges || []).length}</p>
                                </div>
                              </div>

                              {/* Node list preview */}
                              <div className="space-y-1 mb-3 max-h-32 overflow-y-auto">
                                {(diffData.nodes || []).slice(0, 8).map(n => (
                                  <div key={n.id} className="flex items-center gap-2 text-[10px]">
                                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: n.data?.color || "#6366F1" }} />
                                    <span className="text-gray-700 truncate">{n.data?.title || n.type || "Élément"}</span>
                                  </div>
                                ))}
                                {(diffData.nodes || []).length > 8 && (
                                  <span className="text-[9px] text-gray-400">+{(diffData.nodes || []).length - 8} autres</span>
                                )}
                              </div>

                              {!isCurrent && (
                                <button
                                  onClick={() => restore(idx)}
                                  disabled={restoring === idx}
                                  className="w-full py-2 rounded-lg text-[10px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                                  data-testid={`restore-version-${idx}`}
                                >
                                  {restoring === idx ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
                                  Restaurer cette version
                                </button>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-gray-400">Erreur de chargement</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 shrink-0">
          <p className="text-[9px] text-gray-400">
            Les 30 dernières versions sont conservees. Une nouvelle version est créée a chaque sauvegarde.
          </p>
        </div>
      </div>
    </div>
  );
}
